import { VStack, Text, HStack } from 'ui_2';
import part_img from '@assets/part.png';
import { useMemo } from 'react';

// Types for the streak system
interface StreakMilestone {
  daysRequired: number;
  baseReward: number;
  proBonus: number;
}

export interface StreakData {
  currentStreak: number;
  milestones: StreakMilestone[];
}

interface DailyStreakProps {
  streakData: StreakData;
  isPro?: boolean;
}

export const DailyStreak = ({ streakData, isPro }: DailyStreakProps) => {
  const isMilestoneCompleted = (milestone: StreakMilestone) => {
    return streakData.currentStreak >= milestone.daysRequired;
  };

  // Filter to show only the 5 most relevant milestones
  const getRelevantMilestones = (
    milestones: StreakMilestone[],
    currentStreak: number
  ) => {
    // Find the current milestone (next one to achieve)
    const currentIndex = milestones.findIndex(
      (m) => m.daysRequired > currentStreak
    );
    const targetIndex =
      currentIndex === -1 ? milestones.length - 1 : currentIndex;

    // Get 2 before and 2 after the target, plus the target itself
    const startIndex = Math.max(0, targetIndex - 2);
    const endIndex = Math.min(milestones.length, targetIndex + 3);

    let result = milestones.slice(startIndex, endIndex);

    // Fill to exactly 5 nodes
    while (result.length < 5) {
      if (startIndex === 0) {
        // Add from the end
        const nextIndex = endIndex + (5 - result.length);
        result = [...result, ...milestones.slice(endIndex, nextIndex)];
      } else {
        // Add from the beginning
        const prevIndex = Math.max(0, startIndex - (5 - result.length));
        result = [...milestones.slice(prevIndex, startIndex), ...result];
      }
    }

    // Trim to exactly 5
    return result.slice(0, 5);
  };

  const relevantMilestones = useMemo(
    () =>
      getRelevantMilestones(streakData.milestones, streakData.currentStreak),
    [JSON.stringify(streakData)]
  );

  return (
    <div className='w-full'>
      {/* Milestone Nodes with Connecting Line */}
      <div className='relative w-full'>
        {/* Connecting Line */}
        <div className='absolute left-4 right-4 top-3 h-[3px] bg-primary-orange md:top-[13px] lg:left-4 lg:right-4'></div>
        <HStack className=' relative items-center justify-between'>
          {relevantMilestones.map((milestone, index) => (
            <VStack key={index} className='items-center'>
              {/* Milestone Circle */}
              <div
                className={`relative z-10 flex h-6  w-6 items-center justify-center rounded-full border-[1px] font-body text-xs font-normal transition-all duration-300 md:h-[30px] md:w-[30px] ${
                  isMilestoneCompleted(milestone)
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-dashed border-gray-300 bg-white text-gray-500'
                }`}
              >
                {milestone.daysRequired}
              </div>

              {/* Rewards */}
              <VStack className='mt-1 items-center sm:mt-2'>
                {/* Primary Reward */}
                <HStack className='items-center gap-1'>
                  <img
                    src={part_img}
                    width={12}
                    height={12}
                    className='-mt-[1px]'
                  />
                  <Text font='xs' className='text-gray-800 sm:text-sm'>
                    {milestone.baseReward}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          ))}
        </HStack>
      </div>
    </div>
  );
};
